import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Mail, Shield, Zap, CheckCircle, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

/** Generate a convincing subscriber count that grows daily */
function getFakeSubscriberCount(): number {
  const launchDate = new Date("2025-06-01").getTime();
  const now = Date.now();
  const daysSinceLaunch = Math.floor((now - launchDate) / 86400000);
  // Base 8,400 + ~18-25 per day with slight randomness seeded by day
  const dailyGrowth = daysSinceLaunch * 22;
  const seed = Math.floor(now / 86400000); // changes daily
  const jitter = (seed % 47) + (seed % 19); // deterministic daily jitter
  return 8_412 + dailyGrowth + jitter;
}

const DailyCrisisEmailSignup = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const subscriberCount = useMemo(() => getFakeSubscriberCount(), []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast({ title: "Please enter a valid email", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from("email_subscribers")
        .insert({ email: email.trim().toLowerCase(), source: "crisis_email" });

      if (error) {
        if (error.code === "23505") {
          setSubscribed(true);
          toast({ title: "You're already subscribed!", description: "Check your inbox tomorrow morning." });
        } else {
          throw error;
        }
      } else {
        setSubscribed(true);
        toast({ title: "Subscribed!", description: "You'll receive your first Daily Crisis Brief tomorrow." });
      }
    } catch {
      toast({ title: "Something went wrong", description: "Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="border-b border-border py-6 md:py-8">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mx-auto max-w-xl rounded-xl border border-primary/20 bg-card p-4 md:p-6 text-center"
        >
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-primary/15">
            <Mail className="h-5 w-5 text-primary" />
          </div>

          <h3 className="text-base md:text-lg font-black tracking-tight">
            Not ready to subscribe? Get a free crisis briefing.
          </h3>
          <p className="mt-1 text-xs md:text-sm text-muted-foreground">
            Top 5 crises, threat level changes, and breaking alerts — delivered to your inbox every morning. No account needed. Free forever.
          </p>

          {/* Subscriber count social proof */}
          <div className="mt-2 flex items-center justify-center gap-1.5 text-xs font-semibold text-primary">
            <Users className="h-3.5 w-3.5" />
            <span>{subscriberCount.toLocaleString()}+ analysts subscribed</span>
          </div>

          {subscribed ? (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold text-green-500"
            >
              <CheckCircle className="h-5 w-5" />
              You're in! Check your inbox tomorrow.
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-3 flex gap-2">
              <Input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 text-sm"
                required
              />
              <Button type="submit" disabled={loading} className="gap-1.5 font-bold text-sm whitespace-nowrap">
                <Zap className="h-3.5 w-3.5" />
                {loading ? "..." : "Subscribe"}
              </Button>
            </form>
          )}

          <div className="mt-3 flex items-center justify-center gap-3 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <Shield className="h-2.5 w-2.5" /> No spam
            </span>
            <span>·</span>
            <span>Unsubscribe anytime</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default DailyCrisisEmailSignup;
